import { useSetState } from 'ahooks'
import { InfiniteScroll, PullToRefresh, Checkbox, ImageViewer, Steps } from 'antd-mobile'
import classNames from 'classnames'
import { uniq } from 'lodash-es'
import { cloneElement, ReactElement, ReactNode, useLayoutEffect, useRef } from 'react'

import { HErrorBlock } from '@/components'
import { useEventManager } from '@/components/EventManagerProvider'
import usePopup from '@/components/NewEditForm/UILibrary/HYBVant/HPopup/hooks/usePopup'
import { isNotEmptyArray } from '@/utils'

import CardItem from '../CardItem'
import ImageItem from '../CardItem/ImageItem'

import './index.less'

const ImageCardItem = ImageItem(CardItem)
/**
 * 滚动区域组件
 * @returns FC
 */
const ScrollList = ({
  className,
  style,
  before,
  after,
  elementId = 'unifiedFilterWrap',
  dataSource,
  stateStore
}: {
  className?: string
  style?: any
  before?: ReactElement
  after?: ReactElement
  elementId?: string
  dataSource: {
    loading?: boolean | null
    noMore?: boolean
    total?: number
    dataList?: any[]
    elementId?: string
    refresh?: Function
    loadMore: (p?: any) => Promise<any>
    onPullUp?: (p?: any) => Promise<any>
    cardItem?: ReactElement
    renderItem?: (dataSource?: any, trigger?: any) => ReactElement
    bottom?: number
    [key: string]: any
  }
  stateStore?: {
    [x: string]: any
  }
}) => {
  const {
    noMore,
    loading,
    dataList,
    refresh,
    loadMore,
    onPullUp,
    cardItem = dataSource.cardModule === 'ImageCard' ? <ImageCardItem /> : <CardItem />,
    renderItem,
    bottom,
    ...tableOptionRest
  } = dataSource
  const scrollEleRef = useRef(null)
  const noDateRef = useRef(null)
  const { open, modalData, onCancel, onOpen } = usePopup({})
  const [{ marginTop, paddingTop }, setState] = useSetState({ marginTop: 0, paddingTop: 0 })

  const { row_selection, row_selection_code = 'id' } = tableOptionRest

  // 是否可批量操作
  const isRowSelection = typeof row_selection === 'function' ? row_selection() : !!row_selection

  // 选中的
  const { selectedKeys = [] } = stateStore?.tableState || {}

  const renderItemList = () => {
    return dataList?.map((_: any, index) => {
      const extraData = {
        key: `${_.id}_${index}`,
        dataSource: _,
        refresh,
        index,
        isRowSelection,
        ...tableOptionRest
      }
      return renderItem
        ? renderItem(_, { key: extraData.key, refresh, index, isRowSelection, ...tableOptionRest })
        : cloneElement(cardItem, extraData)
    })
  }

  const hasData = isNotEmptyArray(dataList)

  // 获取安全区的高度，包括顶部和底部的安全区
  const getSafeAreaHeight = () => {
    const docEle = getComputedStyle(document.documentElement)
    const topInset = parseFloat(docEle.getPropertyValue('env(safe-area-inset-top)')) || 0
    const bottomInset = parseFloat(docEle.getPropertyValue('env(safe-area-inset-bottom)')) || 0
    return window.innerHeight - topInset - bottomInset
  }

  const calcMarginTop = () => {
    const jsUnifiedFilter = document.getElementById(elementId)
    if (scrollEleRef?.current) {
      const { offsetHeight: filterHeight = 0 } = jsUnifiedFilter || {}
      const { offsetHeight = 0 } = noDateRef.current || {}
      const clientHeight = getSafeAreaHeight()

      setState({
        paddingTop: filterHeight,
        marginTop: (clientHeight - (jsUnifiedFilter?.offsetHeight || 0) - offsetHeight || 0) / 2
      })
    }
  }

  useLayoutEffect(() => {
    if (!hasData && loading === false) {
      calcMarginTop()
    }
  }, [loading, hasData])

  const eventManager = useEventManager()
  useLayoutEffect(() => {
    const jsUnifiedFilter = document.getElementById(elementId)
    // ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      calcMarginTop()
    })

    if (jsUnifiedFilter) {
      resizeObserver.observe(jsUnifiedFilter)
    }

    eventManager?.on('ScrollList.openImage', onOpen)

    return () => {
      eventManager?.off('ScrollList.openImage', onOpen)
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <div className='unified-scroll-list-wrap' style={{ paddingTop: paddingTop }} ref={scrollEleRef}>
      <PullToRefresh
        onRefresh={() => {
          onPullUp?.()
          return refresh?.()
        }}
      >
        <div
          className={classNames('scroll-list-wrapper', className, {
            'scroll-list-no-data-wrapper': !hasData
          })}
          style={style}
        >
          {before}

          {isRowSelection ? (
            <Checkbox.Group
              value={selectedKeys}
              onChange={(values) => {
                const tempValues = uniq(values)
                stateStore?.setTableState?.({
                  selectedKeys: tempValues,
                  selectedItems: dataList?.filter((item) =>
                    tempValues.includes(item[row_selection_code])
                  )
                })
              }}
            >
              {renderItemList()}
            </Checkbox.Group>
          ) : (
            renderItemList()
          )}

          {after}

          {/* {noMore ? <span>--- 我是有底线的 ---</span> : null} */}
          {hasData ? (
            <InfiniteScroll hasMore={!noMore} loadMore={loadMore} threshold={300} />
          ) : (
            loading === false && (
              <div
                className='unified-scroll-list-no-data'
                ref={noDateRef}
                style={{ marginTop: `${marginTop}px` }}
              >
                <HErrorBlock size='large' />
              </div>
            )
          )}
        </div>
      </PullToRefresh>
      <ImageViewer image={modalData?.src} visible={open} onClose={onCancel} />
    </div>
  )
}

export default ScrollList
